import { inject, Injectable } from '@angular/core';
import { type InterpolatableTranslationObject, TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';

export const DEFAULT_LANG: string = 'en-US';

const SUPPORTED_LANGS: readonly string[] = [
  'de-DE',
  'en-US',
  'en-EN',
  'es-ES',
  'fr-FR',
  'it-IT',
  'ja-JP',
  'nl-NL',
];

function getBrowserLanguage(): string | null {
  if (typeof navigator === 'undefined') return null;

  const browserLangs: readonly string[] = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];

  for (const browserLang of browserLangs) {
    if (!browserLang) continue;

    const normalized: string = browserLang.toLowerCase();
    const exactMatch: string | undefined = SUPPORTED_LANGS.find(
      (lang: string) => lang.toLowerCase() === normalized,
    );
    if (exactMatch) return exactMatch;

    const primary: string = normalized.split('-')[0];
    const primaryMatch: string | undefined = SUPPORTED_LANGS.find(
      (lang: string) => lang.split('-')[0].toLowerCase() === primary,
    );

    if (primaryMatch) return primaryMatch;
  }

  return null;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageStorageService {
  private readonly translateService: TranslateService = inject(TranslateService);
  public readonly defaultLang: string = DEFAULT_LANG;

  private currentLang: string | null = null;
  private initialized: boolean = false;

  init(): Observable<InterpolatableTranslationObject | null> {
    if (!this.initialized) {
      this.initialized = true;
      window.addEventListener('storage', this.handleStorageChange);
    }

    return this.applyStoredLanguage();
  }

  private readonly handleStorageChange = (event: StorageEvent): void => {
    if (event.key === 'lang') {
      this.applyStoredLanguage();
    }
  };

  private applyStoredLanguage(): Observable<InterpolatableTranslationObject | null> {
    const nextLang: string =
      localStorage.getItem('lang') || getBrowserLanguage() || this.defaultLang;

    if (nextLang === this.currentLang) return of(null);

    this.currentLang = nextLang;
    return this.translateService.use(nextLang);
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.handleStorageChange);
  }
}
